import { useState, useEffect, useRef } from 'react';
import { useLogin, useNotify, useRefresh, useRedirect } from 'react-admin';
import Parse from 'parse';
import CryptoJS from 'crypto-js';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || 'your-secret-key';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const SHOW_CAPTCHA = process.env.REACT_APP_ENV === 'production';

const Login = () => {
  const login = useLogin();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const recaptchaRef = useRef();

  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  useEffect(() => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      redirect('/');
    }

    // Load remembered credentials
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    const rememberedPassword = localStorage.getItem('rememberedPassword');

    if (rememberedUsername && rememberedPassword) {
      try {
        const decryptedPassword = CryptoJS.AES.decrypt(rememberedPassword, SECRET_KEY).toString(
          CryptoJS.enc.Utf8
        );

        setFormData({
          usernameOrEmail: rememberedUsername,
          password: decryptedPassword,
          rememberMe: true
        });
      } catch (error) {
        console.error('Error decrypting password:', error);
      }
    }
  }, [redirect]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    let updatedFormData = {
      ...formData,
      [name]: name === 'rememberMe' ? checked : value
    };

    // Auto-fill password when email matches saved username
    if (name === 'usernameOrEmail') {
      const rememberedUsername = localStorage.getItem('rememberedUsername');
      const rememberedPassword = localStorage.getItem('rememberedPassword');

      if (value === rememberedUsername && rememberedPassword) {
        try {
          const decryptedPassword = CryptoJS.AES.decrypt(rememberedPassword, SECRET_KEY).toString(
            CryptoJS.enc.Utf8
          );

          updatedFormData = {
            ...updatedFormData,
            password: decryptedPassword,
            rememberMe: true
          };
        } catch (error) {
          console.error('Error decrypting password:', error);
        }
      } else if (value === '') {
        // Clear password if email is cleared
        updatedFormData = {
          ...updatedFormData,
          password: '',
          rememberMe: false
        };
      }
    }

    setFormData(updatedFormData);

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Username or Email is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check CAPTCHA
    if (SHOW_CAPTCHA && !captchaToken) {
      notify('Please complete the CAPTCHA', { type: 'warning' });
      return;
    }

    if (validateForm()) {
      setLoading(true);

      try {
        setCaptchaVerified(true);

        const response = await login({
          username: formData.usernameOrEmail,
          password: formData.password
        });
        console.log(response);

        // Handle remember me
        if (formData.rememberMe) {
          localStorage.setItem('rememberedUsername', formData.usernameOrEmail);
          const encryptedPassword = CryptoJS.AES.encrypt(formData.password, SECRET_KEY).toString();
          localStorage.setItem('rememberedPassword', encryptedPassword);
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberedPassword');
        }

        await refresh();

        if (response?.requiresOtpVerification) {
          notify('OTP has been sent to your registered email address.', { type: 'info' });
          redirect('/admin-otp-verification');
        } else if (response?.role === 'Super-User' || response?.role === 'Admin') {
          redirect('/operatorGames');
        } else {
          redirect('/users');
        }
      } catch (error) {
        notify(error?.message || 'Login failed. Please try again.', { type: 'error' });
        // Reset CAPTCHA on error
        setCaptchaVerified(false);
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1A1A1A',
        backgroundImage: 'url(/Assets/Images/login.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        p: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          backgroundColor: '#000000',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Username/Email Field */}
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '14px',
                    mb: 1,
                    fontWeight: 500
                  }}
                >
                  Username / Email
                </Typography>
                <TextField
                  fullWidth
                  name="usernameOrEmail"
                  placeholder="Enter your username or email"
                  value={formData.usernameOrEmail}
                  onChange={handleChange}
                  error={!!errors.usernameOrEmail}
                  helperText={errors.usernameOrEmail}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#2B2B2B',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFC107'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#fff'
                    }
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '14px',
                    mb: 1,
                    fontWeight: 500
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#2B2B2B',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFC107'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#fff'
                    }
                  }}
                />
              </Box>

              {/* ReCAPTCHA - Only show when enabled and before verification */}
              {SHOW_CAPTCHA && !captchaVerified && RECAPTCHA_SITE_KEY && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => {
                      setCaptchaToken(null);
                      try {
                        recaptchaRef.current?.reset();
                      } catch (err) {
                        console.warn('ReCAPTCHA reset error', err);
                      }
                    }}
                    theme="dark"
                  />
                </Box>
              )}

              {/* Remember Me */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: '#FFC107'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                      Remember Me
                    </Typography>
                  }
                />
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: '#FFC107',
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '16px',
                  py: 1.5,
                  textTransform: 'uppercase',
                  '&:hover': {
                    backgroundColor: '#FFD54F'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 193, 7, 0.5)',
                    color: 'rgba(0, 0, 0, 0.5)'
                  }
                }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
